import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
// --- NEW: Import an icon for the delete button ---
import { ImDownload3, ImUpload, ImBin } from 'react-icons/im';

import { FaTrash } from "react-icons/fa";

const FileUploadComponent = ({ brandingColours, eventId, user }) => {

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const buttonBaseStyle = { // --- NEW: Base style for buttons ---
    cursor: 'pointer',
    border: '1px solid #D9D9D9',
    borderRadius: '3px',
    opacity: 1,
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center', // Center content
    fontWeight: '500',
    width: 'auto', // Let width be flexible
    height: '35px',
    marginLeft: '10px',
    textDecoration: 'none', // For the 'a' tag
    boxSizing: 'border-box', 
    fontFamily: 'inherit',
    fontSize: '15px',
  };

  const downloadButtonStyle = {
    ...buttonBaseStyle, 
    backgroundColor: brandingColours?.primaryColor || '#260F99', 
    color: '#FFFFFF', // Solid white text for the primary button
    border: `1px solid ${brandingColours?.primaryColor || '#260F99'}`,
  };

  const dynamicSecondaryColor = brandingColours?.secondaryColor || '#2A1980';
  
  const deleteButtonStyle = {
    ...buttonBaseStyle, 
    backgroundColor: 'transparent', // Removes the harsh solid red block
    color: dynamicSecondaryColor, // Red text
    border: '1px solid #D9D9D9', // Red outline
  };


  const uploadContainerStyle = {
    border: '2px dashed #ccc',
    padding: '20px',
    textAlign: 'center',
    borderRadius: '5px',
    cursor: 'pointer',
    marginBottom: '20px',
    backgroundColor: isDragging ? '#d0d0d0' : '#e0e0e0', // --- MODIFIED: Visual feedback for dragging ---
    transition: 'background-color 0.2s ease-in-out'
  };

  const uploadTextStyle = {
    margin: 0,
    fontSize: '14px',
    color: '#888'
  };

  const uploadIconStyle = {
    fontSize: '30px',
    marginBottom: '10px'
  };

  const fileItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
    padding: '10px 0'
  };
  
  // --- NEW: Container for the buttons ---
  const actionsContainerStyle = {
    display: 'flex',
    alignItems: 'center',
  };

  const fileInfoStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'Uploaded: Just now';
    const date = new Date(isoDate);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return `Uploaded: ${date.toLocaleDateString('en-US', options)}`;
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getFiles/${eventId}`
      );
      // Ensure the response is an array before setting
      if (Array.isArray(response.data.files)) {
        setUploadedFiles(response.data.files);
      }
    } catch (error) {
      console.error('Failed to fetch files', error);
      setUploadedFiles([]); // Set to empty array on failure
    }
  };

  useEffect(() => {
    if (eventId) { // Only fetch if eventId is available
      fetchFiles();
    }
  }, [eventId]); // --- MODIFIED: Re-run effect if eventId changes ---

  // --- NEW: The delete function ---
  const handleDelete = async (fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const encodedFileName = encodeURIComponent(fileName);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteFile/${eventId}/${encodedFileName}`,
        { withCredentials: true }
      );

      // Refresh the file list from the state to update the UI instantly
      setUploadedFiles(currentFiles => 
        currentFiles.filter(file => file.name !== fileName)
      );
      
      // Optional: Show a success message
      // alert('File deleted successfully.');

    } catch (err) {
      console.error('Failed to delete file', err);
      alert('Could not delete the file. Please try again.');
    }
  };


  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFiles = async (files) => {
    // This example handles one file at a time from a drop/selection.
    // Modify if you allow multiple simultaneous uploads.
    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${user}/uploadFile/${eventId}`,
        formData,
        { withCredentials: true }
      );
      fetchFiles(); // Refresh list after successful upload
    } catch (err) {
      console.error('Upload failed', err);
      alert('File upload failed. Please try again.');
    }
  };

  return (
    <div style={{ marginTop: '15px' }}>
      <div
        style={uploadContainerStyle}
        onDragEnter={handleDragEnter}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          type="file"
          onChange={handleFileChange}
          // If you want to allow multiple file selection, add the 'multiple' prop
          // multiple 
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <div style={uploadIconStyle}><ImUpload /></div>
        <p style={uploadTextStyle}>Drag and drop a file here, or click to select a file</p>
      </div>

      <div>
        {uploadedFiles.map((file) => ( // --- MODIFIED: Use file.key or a unique ID if available ---
          <div key={file.name} style={fileItemStyle}>
            <div style={fileInfoStyle}>
              <p style={{ margin: '0', fontWeight: 'bold' }}>{file.name}</p>
              <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
                {formatDate(file.uploadedAt)}
              </p>
            </div>
            
            {/* --- NEW: Container for action buttons --- */}
            <div style={actionsContainerStyle}>
            <button
                onClick={() => handleDelete(file.name)}
                style={deleteButtonStyle}
              >
                <span style={{ marginRight: '7px', color: dynamicSecondaryColor}}>
                  <FaTrash />
                </span>
                Delete
              </button>
              <a
                href={file.url}
                download={file.name}
                target="_blank" // Good practice for downloads
                rel="noopener noreferrer"
                style={downloadButtonStyle}
              >
                <span style={{ marginRight: '7px' }}>
                  <ImDownload3 />
                </span>
                Download
              </a>

              {/* --- NEW: Delete button --- */}
              
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileUploadComponent;